import cx from 'classnames';
import { InputHTMLAttributes, PropsWithChildren, useEffect, useId, useRef } from 'react';

import TextBox from './TextBox';
import { checkboxSizes, CheckboxSizeType } from './utils';
import { defaultHoverTransition } from '../../utils/utils';

const indeterminateStyles =
  'indeterminate:bg-primaryComp-50 indeterminate:hover:bg-primaryComp-50 indeterminate:focus:bg-primaryComp-50 indeterminate:hover:ring-2';

const variants = {
  contrast: `checked:bg-primaryComp-50 checked:hover:bg-primaryComp-50 checked:focus:bg-primaryComp-50 ${indeterminateStyles} indeterminate:hover:ring-white20 bg-transparent border-white40 hover:bg-white10 hover:border-complementary-50 checked:hover:ring-2 checked:hover:ring-white20 disabled:!bg-gray-80 disabled:!border-gray-70`,
  regular: `checked:bg-primaryComp-50 checked:hover:bg-primaryComp-50 checked:focus:bg-primaryComp-50 ${indeterminateStyles} indeterminate:hover:ring-primaryComp-20 bg-white border-gray-40 hover:bg-gray-10 hover:border-complementary-50 checked:hover:ring-2 checked:hover:ring-primaryComp-20 disabled:bg-gray-30 disabled:border-gray-40`,
};

export type CheckboxVariant = keyof typeof variants;

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  description?: string;
  size?: CheckboxSizeType;
  checked: boolean;
  partiallySelected?: boolean;
  hasError?: boolean;
  variant?: CheckboxVariant;
  className?: string;
  containerClassName?: string;
  disabled?: boolean;
}

const Checkbox = ({
  children,
  description,
  size = 'md',
  hasError,
  className,
  containerClassName,
  variant = 'regular',
  disabled,
  partiallySelected = false,
  checked,
  ...props
}: PropsWithChildren<CheckboxProps>) => {
  const { checkboxSize } = checkboxSizes[size];
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  const { id, ...inputProps } = props;
  const inputId = id ?? generatedId;

  useEffect(() => {
    if (!inputRef.current) return;

    inputRef.current.indeterminate = Boolean(partiallySelected && !checked);
  }, [partiallySelected, checked]);

  return (
    <div className={cx('flex space-x-3', containerClassName)}>
      <input
        ref={inputRef}
        {...inputProps}
        id={inputId}
        type="checkbox"
        checked={checked}
        className={cx(
          'cursor-pointer rounded border outline-0 focus:ring-0 focus:ring-offset-0',
          { '!border-red-500': hasError, 'pointer-events-none': disabled },
          defaultHoverTransition,
          variants[variant],
          checkboxSize,
          className
        )}
        disabled={disabled}
        aria-checked={partiallySelected && !checked ? 'mixed' : undefined}
      />
      {children && (
        <TextBox>
          <TextBox.Label size={size} disabled={disabled} htmlFor={inputId}>
            {children}
          </TextBox.Label>
          {description && <TextBox.Description description={description} size={size} disabled={disabled} />}
        </TextBox>
      )}
    </div>
  );
};

export default Checkbox;
